/**
 * RemindersPage — the /reminders screen. List the household's reminders, add a new
 * one, and check them off. A reminder fires into the notification bell when it comes
 * due (the cron does that); this is where they're created and managed.
 *
 * The frontend split is a courtesy: a child sees the audience options they're
 * allowed (everyone / just me — never adults-only), but worker/routes/reminders.ts
 * is what actually enforces it. A failed save never loses what you typed.
 *
 * Prop-free by design — the integrator drops it straight onto a /reminders route.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiRequestError } from '../../lib/api';
import { useIdentity } from '../../lib/identity';
import type { Reminder, ReminderInput, MemberSummary } from '../../shared/types';
import { formatDue, parseTs, defaultDueLocal } from './util';

type Audience = Reminder['audience'];
type Recurrence = NonNullable<Reminder['recurrence']> | '';

export function RemindersPage() {
  const identity = useIdentity();
  const isAdult = identity.role === 'adult';

  const [items, setItems] = useState<Reminder[] | null>(null);
  const [members, setMembers] = useState<MemberSummary[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [reminders, roster] = await Promise.all([
        api.get<Reminder[]>('/reminders'),
        api.get<MemberSummary[]>('/reminders/members'),
      ]);
      setItems(reminders);
      setMembers(roster);
    } catch (err) {
      setLoadError(
        err instanceof ApiRequestError
          ? err.message
          : 'We could not reach the house. Check your connection and try again.'
      );
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const nameFor = useCallback(
    (memberId: number | null): string => {
      if (memberId == null) return 'A family member';
      if (memberId === identity.memberId) return 'You';
      return members.find((m) => m.id === memberId)?.name ?? 'A family member';
    },
    [members, identity.memberId]
  );

  const todo = useMemo(
    () => (items ?? []).filter((r) => !r.done_at),
    [items]
  );
  const done = useMemo(
    () => (items ?? []).filter((r) => r.done_at),
    [items]
  );

  function upsert(next: Reminder) {
    setItems((prev) => {
      const list = prev ?? [];
      const exists = list.some((r) => r.id === next.id);
      return exists ? list.map((r) => (r.id === next.id ? next : r)) : [next, ...list];
    });
  }

  async function setDone(r: Reminder, doneNow: boolean) {
    setActionError(null);
    setBusyId(r.id);
    try {
      const updated = await api.post<Reminder>(
        `/reminders/${r.id}/${doneNow ? 'done' : 'undone'}`,
        {}
      );
      upsert(updated);
    } catch (err) {
      setActionError(
        err instanceof ApiRequestError
          ? err.message
          : "Couldn't update that reminder. Try again in a moment."
      );
    } finally {
      setBusyId(null);
    }
  }

  async function remove(r: Reminder) {
    if (!window.confirm(`Remove "${r.title}"? This can't be undone.`)) return;
    setActionError(null);
    setBusyId(r.id);
    try {
      await api.del(`/reminders/${r.id}`);
      setItems((prev) => (prev ?? []).filter((x) => x.id !== r.id));
    } catch (err) {
      setActionError(
        err instanceof ApiRequestError
          ? err.message
          : "Couldn't remove that reminder. Try again in a moment."
      );
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold text-ink">Reminders</h1>
        <p className="mt-1 text-sm text-ink-soft">
          A nudge that lands in the notification bell when it's time.
        </p>
      </header>

      <NewReminder isAdult={isAdult} members={members} onCreated={upsert} />

      {actionError && (
        <p
          role="alert"
          className="rounded-card border border-clay/30 bg-clay-soft px-4 py-3 text-sm text-clay"
        >
          {actionError}
        </p>
      )}

      {items === null && !loadError && <ListSkeleton />}

      {loadError && (
        <div className="rounded-card border border-border bg-surface p-8 text-center">
          <h2 className="font-display text-lg text-ink">We couldn't load your reminders</h2>
          <p className="mx-auto mt-2 max-w-sm text-sm text-ink-soft">{loadError}</p>
          <button
            type="button"
            onClick={() => {
              setItems(null);
              void load();
            }}
            className="mt-5 inline-flex min-h-11 items-center rounded-full bg-sage px-5 text-sm font-medium text-white transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dusk"
          >
            Try again
          </button>
        </div>
      )}

      {items !== null && !loadError && items.length === 0 && (
        <div className="rounded-card border border-dashed border-border bg-surface p-8 text-center">
          <h2 className="font-display text-lg text-ink">No reminders yet</h2>
          <p className="mx-auto mt-2 max-w-sm text-sm text-ink-soft">
            Add the first one above — trash night, a permission slip, medicine at 8. It'll show up
            in the bell right when it's due.
          </p>
        </div>
      )}

      {items !== null && todo.length > 0 && (
        <Section title="To do">
          {todo.map((r) => (
            <ReminderRow
              key={r.id}
              r={r}
              audienceLabel={audienceLabel(r, nameFor)}
              canRemove={isAdult}
              busy={busyId === r.id}
              onDone={() => setDone(r, true)}
              onRemove={() => remove(r)}
            />
          ))}
        </Section>
      )}

      {items !== null && done.length > 0 && (
        <Section title="Done">
          {done.map((r) => (
            <ReminderRow
              key={r.id}
              r={r}
              audienceLabel={audienceLabel(r, nameFor)}
              canRemove={isAdult}
              busy={busyId === r.id}
              onUndo={() => setDone(r, false)}
              onRemove={() => remove(r)}
            />
          ))}
        </Section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- create form

function NewReminder({
  isAdult,
  members,
  onCreated,
}: {
  isAdult: boolean;
  members: MemberSummary[];
  onCreated: (r: Reminder) => void;
}) {
  const identity = useIdentity();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [dueLocal, setDueLocal] = useState(() => defaultDueLocal());
  const [audience, setAudience] = useState<Audience>('all');
  const [memberId, setMemberId] = useState<number | ''>(identity.memberId ?? '');
  const [recurrence, setRecurrence] = useState<Recurrence>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!title.trim()) {
      setError('Give the reminder a title.');
      return;
    }
    if (!dueLocal) {
      setError('Pick a date and time.');
      return;
    }
    const due = new Date(dueLocal);
    if (Number.isNaN(due.getTime())) {
      setError('That date and time did not read right.');
      return;
    }

    let targetMember: number | null = null;
    if (audience === 'member') {
      targetMember = isAdult ? (typeof memberId === 'number' ? memberId : null) : identity.memberId;
      if (targetMember == null) {
        setError('Pick who this reminder is for.');
        return;
      }
    }

    const payload: ReminderInput = {
      title: title.trim(),
      body: body.trim() ? body.trim() : null,
      due_at: due.toISOString(),
      audience,
      member_id: targetMember,
      recurrence: recurrence ? recurrence : null,
    };

    setSaving(true);
    try {
      const created = await api.post<Reminder>('/reminders', payload);
      onCreated(created);
      // Keep the audience/recurrence choices; clear the one-off fields.
      setTitle('');
      setBody('');
      setDueLocal(defaultDueLocal());
    } catch (err) {
      // Never lose what they typed — leave every field as-is.
      setError(
        err instanceof ApiRequestError
          ? err.message
          : "Couldn't save that reminder. Try again in a moment."
      );
    } finally {
      setSaving(false);
    }
  }

  const inputClass =
    'min-h-11 w-full rounded-lg border border-border bg-surface px-3 text-sm text-ink placeholder:text-ink-faint focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dusk';

  return (
    <form
      onSubmit={submit}
      className="space-y-4 rounded-card border border-border bg-surface p-5 shadow-sm"
    >
      <div>
        <label htmlFor="reminder-title" className="mb-1 block text-sm font-medium text-ink">
          What's the reminder?
        </label>
        <input
          id="reminder-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Take out the trash"
          maxLength={120}
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor="reminder-body" className="mb-1 block text-sm font-medium text-ink">
          Details <span className="font-normal text-ink-faint">(optional)</span>
        </label>
        <textarea
          id="reminder-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Green bin this week"
          rows={2}
          maxLength={2000}
          className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dusk"
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="reminder-due" className="mb-1 block text-sm font-medium text-ink">
            When
          </label>
          <input
            id="reminder-due"
            type="datetime-local"
            value={dueLocal}
            onChange={(e) => setDueLocal(e.target.value)}
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="reminder-recurrence" className="mb-1 block text-sm font-medium text-ink">
            Repeat
          </label>
          <select
            id="reminder-recurrence"
            value={recurrence}
            onChange={(e) => setRecurrence(e.target.value as Recurrence)}
            className={inputClass}
          >
            <option value="">Just once</option>
            <option value="daily">Every day</option>
            <option value="weekly">Every week</option>
            <option value="monthly">Every month</option>
          </select>
        </div>

        <div>
          <label htmlFor="reminder-audience" className="mb-1 block text-sm font-medium text-ink">
            Who sees it
          </label>
          <select
            id="reminder-audience"
            value={audience}
            onChange={(e) => setAudience(e.target.value as Audience)}
            className={inputClass}
          >
            <option value="all">Everyone</option>
            {isAdult && <option value="adults">Just the grown-ups</option>}
            <option value="member">One person</option>
          </select>
        </div>

        {audience === 'member' && isAdult && (
          <div>
            <label htmlFor="reminder-member" className="mb-1 block text-sm font-medium text-ink">
              For
            </label>
            <select
              id="reminder-member"
              value={memberId}
              onChange={(e) => setMemberId(e.target.value ? Number(e.target.value) : '')}
              className={inputClass}
            >
              <option value="">Pick a person…</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.id === identity.memberId ? `${m.name} (you)` : m.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {audience === 'member' && !isAdult && (
          <p className="self-end text-sm text-ink-soft sm:pb-3">This one is just for you.</p>
        )}
      </div>

      {error && (
        <p
          role="alert"
          className="rounded-lg border border-clay/30 bg-clay-soft px-3 py-2 text-sm text-clay"
        >
          {error}
        </p>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex min-h-11 items-center rounded-full bg-sage px-5 text-sm font-medium text-white transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dusk disabled:opacity-60"
        >
          {saving ? 'Adding…' : 'Add reminder'}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------- rows

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section aria-label={title}>
      <h2 className="mb-2 text-xs font-semibold tracking-wide text-ink-faint uppercase">{title}</h2>
      <ul className="space-y-2">{children}</ul>
    </section>
  );
}

function ReminderRow({
  r,
  audienceLabel,
  canRemove,
  busy,
  onDone,
  onUndo,
  onRemove,
}: {
  r: Reminder;
  audienceLabel: string;
  canRemove: boolean;
  busy: boolean;
  onDone?: () => void;
  onUndo?: () => void;
  onRemove: () => void;
}) {
  const due = parseTs(r.due_at);
  const isDone = !!r.done_at;
  const overdue = !isDone && due.getTime() < Date.now();

  return (
    <li className="flex items-start gap-3 rounded-card border border-border bg-surface p-4">
      <button
        type="button"
        onClick={isDone ? onUndo : onDone}
        disabled={busy}
        aria-label={isDone ? `Mark "${r.title}" not done` : `Mark "${r.title}" done`}
        aria-pressed={isDone}
        className="group -m-1 grid h-11 w-11 shrink-0 place-items-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dusk disabled:opacity-50"
      >
        <span
          className={`grid h-6 w-6 place-items-center rounded-full border-2 transition-colors ${
            isDone
              ? 'border-sage bg-sage text-white'
              : 'border-ink-faint bg-surface group-hover:border-sage'
          }`}
        >
          {isDone && (
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="m5 12 5 5 9-10" />
            </svg>
          )}
        </span>
      </button>

      <div className="min-w-0 flex-1">
        <p className={`text-sm font-semibold ${isDone ? 'text-ink-faint line-through' : 'text-ink'}`}>
          {r.title}
        </p>
        {r.body && <p className="mt-0.5 text-sm text-ink-soft">{r.body}</p>}

        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-faint">
          <time dateTime={due.toISOString()}>{formatDue(due)}</time>
          {overdue && (
            <span className="rounded-full bg-clay-soft px-1.5 py-0.5 font-semibold text-clay">
              Overdue
            </span>
          )}
          {r.recurrence && (
            <>
              <span aria-hidden="true">·</span>
              <span>{recurrenceLabel(r.recurrence)}</span>
            </>
          )}
          <span aria-hidden="true">·</span>
          <span>{audienceLabel}</span>
        </div>
      </div>

      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          disabled={busy}
          className="inline-flex min-h-11 shrink-0 items-center rounded-lg px-3 text-xs font-medium text-ink-faint transition-colors hover:bg-surface-sunk hover:text-clay focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dusk disabled:opacity-50"
        >
          Remove
        </button>
      )}
    </li>
  );
}

function ListSkeleton() {
  return (
    <ul className="space-y-2" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <li key={i} className="flex animate-pulse items-start gap-3 rounded-card bg-surface p-4">
          <span className="mt-0.5 h-6 w-6 shrink-0 rounded-full bg-surface-sunk" />
          <span className="flex-1 space-y-2 py-1">
            <span className="block h-3 w-2/5 rounded bg-surface-sunk" />
            <span className="block h-3 w-3/5 rounded bg-surface-sunk" />
          </span>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------- labels

function recurrenceLabel(recurrence: NonNullable<Reminder['recurrence']>): string {
  switch (recurrence) {
    case 'daily':
      return 'Every day';
    case 'weekly':
      return 'Every week';
    case 'monthly':
      return 'Every month';
  }
}

function audienceLabel(r: Reminder, nameFor: (id: number | null) => string): string {
  switch (r.audience) {
    case 'all':
      return 'Everyone';
    case 'adults':
      return 'Grown-ups';
    case 'member':
      return `Just ${nameFor(r.member_id)}`;
  }
}
